import { useCallback, useRef, useState } from 'react';
import { todayISO } from '../domain/clock';
import { clampAmountDecimals } from '../domain/money';
import { attachmentPaths } from '../domain/expenses';
// The form's rules — the blank form, the dirty check, what Save refuses, what
// Save writes, the scanned form — are `src/domain/expenseForms.js`'s,
// re-exported here for the callers that import them from the hook.
import {
  blankEntryForm, isEntryDirty, entryErrors, entryCanSave, entryAmountMinor, errorsAfterPatch,
  attachRefusal, scannedEntryForm, formForEntry,
} from '../domain/expenseForms';
import { useCurrency, useDatePattern, useLocale } from '../context/LocaleContext';
// `scanTooLarge` from fileLimits.js, which reads no build-time configuration,
// so jest can load this hook (#106).
import {
  FileRefused, orphanedReceipts, refuseFile, scanTooLarge, uploadErrorMessage, warnUnsweptReceipts,
} from '../lib/fileLimits';
import useUploads from './useUploads';

// The add/edit flow: the drawer stack, the form behind it, the unsaved-changes
// guard, and the two ways a receipt gets attached.
//
// One hook rather than three because they are one thing — scanning a receipt
// opens the form, the form's dirty state decides whether closing it is
// allowed, and saving closes the stack.
//
// The OCR client arrives as an argument (#106), from the page, as `addEntry`,
// `updateEntry`, `uploadAttachment` and `removeAttachment` do: receiptOcr.js
// reads `import.meta.env`, a parse error under jest, so importing it would make
// this hook unloadable by every jest project. The hook depends on no
// build-time configuration and no Firebase handle, so its test needs neither
// Vite nor an emulator.
//
// The default is the sentence receiptOcr itself throws when
// VITE_RECEIPT_OCR_URL is unset: no scanner was supplied.

const noScanner = async () => { throw new Error('Receipt scanning is not set up.'); };

const today = todayISO;

export const blankForm = () => blankEntryForm(today());
export const isDirty = (form, baseline) => isEntryDirty(form, baseline, today());
export {
  REQUIRED, AMOUNT_TOO_LARGE, AMOUNT_NEGATIVE, ATTACHMENTS_FULL, entryErrors, formForEntry,
} from '../domain/expenseForms';

export default function useExpenseEntryForm({
  addEntry, updateEntry, uploadAttachment,
  removeAttachment = async () => {}, extractReceipt = noScanner,
  categoriesFor = () => [],
}) {
  // The add flow is one stack of panels: MMS pushes New Category over Add
  // Expense over the picker, and the back arrow walks down it with each panel's
  // state intact.
  // What a typed amount means, from the studio's locale (src/domain/locale.js).
  const currency = useCurrency();
  // And how the studio writes a date, which is how a scanned receipt's
  // numeric date is read (#501).
  const datePattern = useDatePattern();
  // And its decimal separator, which the sizes in a refusal are written with
  // (`13,4 MB` in Germany, #583).
  const { decimalSeparator } = useLocale();
  const [step, setStep] = useState(null); // null | 'picker' | 'form' | 'category'
  const [type, setType] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [editing, setEditing] = useState(null); // { id, baseline }
  const [saving, setSaving] = useState(null); // null | 'save' | 'another'
  const [guard, setGuard] = useState(null); // pending navigation, held
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [attachError, setAttachError] = useState(null);
  // The receipt the scan is sending up beside the reader, while it goes:
  // `{ key, name, status, transferred, total }`, drawn under Scan a Receipt
  // by the upload row every site uses (#612).
  const [scanUpload, setScanUpload] = useState(null);
  // What the last Save refused, keyed by field. Empty until the first submit;
  // see `entryErrors` for why this is submit-time and not live.
  const [errors, setErrors] = useState({});

  // Amount truncates a third decimal rather than rounding it (#308):
  // `fromDecimal` rounds half-up, so `50.999` would be filed at $51.00.
  // `clampAmountDecimals` is MMS's own editor on its Add Payment drawer (#246):
  // the third keystroke is swallowed.
  //
  // It does not touch the sign: `entryErrors` refuses a typed minus where MMS
  // silently strips it (#166, a knowing divergence), and `-50.999` clamps to
  // `-50.99`, so the refusal still sees its minus.
  //
  // Keyed on `amount` because this setter carries the payee, the category, the
  // dates and the recurrence too. The receipt-scan path builds a whole form with
  // `setForm` and needs no clamp: its amount comes from `toAmountField` over
  // minor units.
  const patch = (fields) => {
    setForm((f) => ({
      ...f,
      ...fields,
      ...('amount' in fields
        ? { amount: clampAmountDecimals(fields.amount, currency) }
        : null),
    }));
    // `errorsAfterPatch` says which refusals the change clears.
    setErrors((prev) => errorsAfterPatch(prev, Object.keys(fields)));
  };

  // ── Receipt objects this session has put in the bucket (#94) ──────────────
  //
  // AttachmentField's Remove clears the form field only, and an uploaded object
  // is charged against the studio's byte ceiling (#62), so the sweep needs to
  // know every path this sitting uploaded.
  //
  // A ref: nothing on screen reads it, and state would re-render the drawer in
  // the middle of an upload for a bookkeeping list.
  const uploaded = useRef([]);

  // The receipts on their way up (#612): each with its bar and amount, a
  // failed one with its sentence and Try again, × to cancel — the library's
  // list (src/hooks/useUploads.js) over `uploadAttachment`, one at a time.
  // Each lands on the form as it lands, recorded first so the sweep knows the
  // object even if everything after it fails.
  //
  // A failure is said through the mapper rather than as `err.message`: the
  // SDK's own sentence for a refused write names a bucket path the teacher has
  // never seen. A cancel passes through, to say nothing.
  const sendReceipt = useCallback(
    (file, { onProgress, signal }) => {
      let sent;
      try {
        sent = Promise.resolve(uploadAttachment(file, { onProgress, signal }));
      } catch (err) {
        sent = Promise.reject(err);
      }
      return sent.then(null, (err) => {
        if (err?.cancelled) throw err;
        throw new FileRefused(uploadErrorMessage(err));
      });
    },
    [uploadAttachment],
  );
  const receipts = useUploads(sendReceipt, {
    onLanded: (attachment) => {
      if (!attachment?.path) return;
      uploaded.current.push(attachment.path);
      // Appended through the setter, not over a captured `form`: two landing
      // across awaits would otherwise both read the list as it was, and the
      // second would drop the first, orphaning that object behind a card that
      // says it is attached.
      setForm((f) => ({ ...f, attachments: [...f.attachments, attachment] }));
    },
  });

  // Whose receipt is this — the sitting's, or a row's?
  //
  // An attachment reaches `form.attachments` either through `attach`/
  // `scanReceipt`, which upload an object and record its path here, or through
  // `openEdit`/`formForEntry`, which copy the entry's. So a path in the list
  // was put in the bucket by this sitting and the sweep disposes of it; a path
  // not in it came off a document, and a row still points at it. It filters a
  // list (#104), because a form can hold both kinds at once.
  const sessionUploads = (list) =>
    (list || []).filter((a) => a?.path && uploaded.current.includes(a.path));

  // Take away the objects nothing points at any more.
  //
  // After the row is written, never before: an object deleted ahead of a save
  // that then fails leaves a saved row naming a path with nothing behind it.
  // So every caller runs this only once its write has resolved, and a throw
  // from `updateEntry` or `addEntry` skips it, leaving the object for the
  // retry to save.
  //
  // Best-effort in the other direction too: `removeAttachment` swallows
  // `storage/object-not-found` and reports anything else rather than throwing,
  // because refusing to finish saving an expense over a storage error is the
  // wrong trade. A path the bucket refused to delete stays charged against
  // `bytesHeld` with no row naming it, so it is named by `warnUnsweptReceipts`
  // (a console line; the durable report is the server's reconcile, see
  // lib/fileLimits.js, #140). The loop does not stop on a failure.
  //
  // Both arguments are lists (#104): `orphanedReceipts` compares every kept
  // path against every path this sitting touched, so a two-receipt expense
  // saved unchanged deletes nothing.
  const sweepReceipts = async (keptPaths, savedPaths) => {
    const orphans = orphanedReceipts({ uploaded: uploaded.current, savedPaths, keptPaths });
    // Cleared synchronously, before the first await: whatever the teacher does
    // next starts with an empty list rather than one this sweep is still
    // walking.
    uploaded.current = [];
    const unswept = [];
    for (const path of orphans) {
      // No news is good news, so a fake or the no-op default reads as
      // success.
      if (await removeAttachment(path)) unswept.push(path);
    }
    warnUnsweptReceipts(unswept);
    return unswept;
  };

  const openPicker = () => setStep('picker');

  // ── Opening a new entry: what is kept, rather than what is cleared (#139) ──
  //
  // The form is rebuilt from `blankForm()`, and this says what is carried
  // across: nothing from an edited entry, so neither its receipt (#107) nor its
  // payee, amount, dates or recurrence (#139) can leak into a "New Expense". A
  // field added to the form is dropped here by default.
  //
  // What is carried is a receipt this sitting uploaded, and only that
  // (`sessionUploads`): dropping it would silently bin an upload the teacher
  // just made. The category is not carried: switching between expense and
  // revenue swaps the category vocabulary.
  //
  // Neither walk is reachable from the screen: EntryFormDrawer passes
  // `onBack={editing ? null : onBack}`, so an edit has no back arrow, every
  // other exit goes through `closeAll`, which blanks the form, and a dirty new
  // form's back arrow raises the guard. The rule holds here regardless.
  //
  // Whether MMS's "Add Expense" re-entered from the picker is a fresh form or
  // the panel left is unchecked (#139). Keeping the panel would mean keeping
  // `editing` with it — re-entering the edit — which is a change to the whole
  // stack rather than to this function.
  const openForm = (nextType) => {
    setType(nextType);
    setEditing(null);
    setScan(null);
    setAttachError(null);
    setErrors({});
    setForm((f) => ({
      ...blankForm(),
      attachments: sessionUploads(f.attachments),
    }));
    setStep('form');
  };

  // `attachments` is required here even though `formForEntry` defaults it:
  // a forgotten list would open the drawer with no receipt cards and then take
  // every object off the entry on the first save, since an empty list means
  // "the teacher removed them all". A throw, because this can only be a wiring
  // mistake (#183).
  const openEdit = (entry, attachments) => {
    if (!Array.isArray(attachments)) {
      throw new TypeError(
        'openEdit needs the entry\'s attachments as a second argument — pass '
        + '`useExpenses().attachmentsFor(entry.instanceOf || entry.id)`. Without '
        + 'them the drawer opens with no receipts and the first save detaches '
        + 'every one of them (#183).',
      );
    }
    const next = formForEntry(entry, currency, attachments);
    receipts.clear();
    setScan(null);
    setAttachError(null);
    // An entry can be carrying an unset category (#116); opening it must not
    // greet the teacher with a red field they have not touched. The refusal
    // arrives at Save.
    setErrors({});
    setType(entry.type);
    // The baseline is the entry as loaded, so the guard fires on a real change
    // rather than on the fields being populated.
    setEditing({ id: entry.instanceOf || entry.id, baseline: next });
    setForm(next);
    setStep('form');
  };

  // Leaving without saving. The entry keeps the receipts it already had — the
  // reason the delete does not happen on the button press — so `savedPaths` is
  // also what survives, and everything this session uploaded goes. For an
  // entry never saved that is every upload: "attach, then cancel".
  const closeAll = () => {
    const savedPaths = attachmentPaths(editing?.baseline);
    // A receipt still going up is cancelled; one that lands despite that is
    // deleted by `uploadAttachment` itself, since nothing will name it.
    receipts.clear();
    setStep(null); setType(null); setEditing(null); setScan(null); setForm(blankForm());
    setErrors({});
    return sweepReceipts(savedPaths, savedPaths);
  };

  // Every exit from a dirty form routes through here. `next` is what to do once
  // the question is answered, held rather than run so Cancel can drop it.
  const leaveForm = (next) => {
    if (isDirty(form, editing?.baseline)) { setGuard(() => next); return; }
    next();
  };

  // Whether Save is clickable, which is not whether it will go through — see
  // `entryCanSave`.
  const canSave = entryCanSave(form);

  const save = async (andAnother) => {
    const refusals = entryErrors(form, currency);
    setErrors(refusals);
    // The drawer stays open with the fields marked, as MMS does. Nothing is
    // written and nothing is swept.
    if (Object.keys(refusals).length) return false;
    setSaving(andAnother ? 'another' : 'save');
    const payload = { ...form, type, amountMinor: entryAmountMinor(form.amount, currency) };
    // What a row will name once this write lands. Everything else this session
    // uploaded, and any receipt the entry is edited away from, is what the
    // sweep takes; both calls sit after their write.
    const keptPaths = attachmentPaths(payload);
    try {
      if (editing) {
        const savedPaths = attachmentPaths(editing.baseline);
        await updateEntry(editing.id, payload);
        receipts.clear();
        setEditing(null);
        setForm(blankForm());
        setStep(null); setType(null);
        await sweepReceipts(keptPaths, savedPaths);
        return true;
      }
      await addEntry(payload);
      receipts.clear();
      setForm(blankForm());
      if (!andAnother) { setStep(null); setType(null); }
      // No saved path: a new entry pointed at nothing. The kept path leaves
      // the list because a row now owns it; left behind, the next form's cancel
      // could delete a saved entry's receipt.
      await sweepReceipts(keptPaths, []);
      return true;
    } finally { setSaving(null); }
  };

  // Attaching without scanning — a receipt can be filed whether or not
  // anything is read off it.
  //
  // Adding, not replacing (#104): MMS lists every receipt as its own card
  // under the paperclip. The picker is `multiple`; whether MMS's own picker
  // takes several in one press is unchecked, and restricting ours to one file
  // would reach the same list in more presses.
  //
  // Sequential: each upload spends one of the studio's daily authorizations in
  // a transaction that also checks the byte ceiling, so overlapping uploads
  // could each be told there is room. `receipts` sends them one at a time.
  //
  // A failed upload stays listed with its sentence and Try again (#612), as in
  // the library and both phones, and the rest of the batch still goes. A
  // refusal the callable makes (the allowance, the ceiling) spends nothing.
  // `attachError` is only what is refused before any bytes move: the count and
  // the core's file refusal.
  //
  // Resolves when the batch has gone up or failed.
  const attach = async (files) => {
    const picked = (Array.isArray(files) ? files : [files]).filter(Boolean);
    if (!picked.length) return;
    setAttachError(null);
    // How many the form holds, counting the ones still going up and the ones
    // that failed — Try again would put those on it too.
    let held = (form.attachments || []).length + receipts.items.length;
    const accepted = [];
    for (const file of picked) {
      // The count, refused with a sentence rather than clamped in silence
      // (#115). The clamp is the backstop for a caller with no form in front
      // of it.
      const full = attachRefusal(held);
      if (full) { setAttachError(full); break; }

      // Size and type against the rule's own numbers (#62), so the refusal
      // arrives without a round trip; `uploadAttachment` checks both again.
      // What was picked before the refused file still goes.
      const refusal = refuseFile(file, decimalSeparator);
      if (refusal) { setAttachError(refusal); break; }
      accepted.push(file);
      held += 1;
    }
    await receipts.add(accepted);
  };

  // Taking a receipt off the form, which is not taking it out of the bucket:
  // the entry may not be saved at all, and deleting on the press would destroy
  // the receipt of an edit the teacher then cancels. `uploaded.current` still
  // names it if this sitting uploaded it, and `sweepReceipts` disposes of it at
  // save or at abandon (#94).
  //
  // By index rather than by path, so the card pressed is the one removed
  // without relying on paths being unique.
  const detach = (index) => {
    setAttachError(null);
    setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== index) }));
  };

  // A scanned receipt fills the form and gets out of the way. It is never
  // saved automatically: OCR on a phone photo is frequently wrong.
  const scanReceipt = async (file) => {
    if (!file) return;

    // Asked here rather than left to `extractReceipt` (#96): the OCR service
    // refuses at 12 MB and storage at 2 GB, so a 13 MB photo would have the
    // extraction fail while the upload beside it succeeds. Refusing up front
    // moves no bytes at all. Other extraction failures after the upload has
    // landed are handled below (#119).
    const tooBig = scanTooLarge(file, decimalSeparator);
    if (tooBig) { setScan({ error: tooBig }); setStep('picker'); return; }

    setScanning(true);
    setScan(null);
    setAttachError(null);
    const scanKey = `scan.${Date.now()}`;
    setScanUpload({
      key: scanKey, name: file.name || 'receipt', status: 'uploading', transferred: 0, total: file.size || 0,
    });
    try {
      // OCR and the upload run together: the scan is worth little without the
      // image to check it against, and in sequence the wait doubles.
      //
      // A failed upload must not lose the extraction, but the teacher is told
      // (#62): otherwise they save an entry believing its evidence is filed.
      // Held in a local, because if the extraction failed the form never opens
      // and a set message would be left for whatever opens next.
      let uploadFailure = null;
      // Neither rejection may discard the other's result (#119). An extraction
      // that fails — the service down, a 5xx, an unparseable reply — can do so
      // after the upload has landed, so both are caught and the object is
      // recorded below before either outcome is acted on. From there the sweep
      // owns it: closing the picker calls `closeAll`, which takes it.
      //
      // A failed scan returns to the picker rather than opening the form with
      // the receipt attached: `ScanNotice` returns null on `scan.error`, so the
      // form has nowhere to put the reason (#119).
      const [scanned, attachment] = await Promise.all([
        // Read for this studio: its currency's places and its date order
        // (#501), or a ¥1,200 receipt fills ¥120,000.
        extractReceipt(file, { currency, datePattern, decimal: decimalSeparator }).then(
          (value) => ({ ok: true, value }),
          (error) => ({ ok: false, error }),
        ),
        uploadAttachment(file, {
          onProgress: ({ transferred, total }) => setScanUpload((u) => (u?.key === scanKey ? {
            ...u, transferred, total: total || u.total,
          } : u)),
        }).catch((err) => {
          // The raw error is kept in the console: the teacher is shown a
          // mapped sentence.
          console.warn('[expenses] receipt upload failed', err);
          uploadFailure = err;
          return null;
        }),
      ]);
      // As in `attach`, and before the branch (#119): an object that landed is
      // recorded whether or not the reading worked, or nothing would ever
      // remove it.
      if (attachment?.path) uploaded.current.push(attachment.path);

      if (!scanned.ok) {
        // The picker, with the reason on it. `attachError` is not set: the
        // form never opens, so it would wait for whatever opens next.
        setScan({ error: scanned.error?.message || 'The receipt could not be read.' });
        setStep('picker');
        return;
      }
      const result = scanned.value;

      // A failed upload is a failed row on the form, with its sentence and
      // Try again (#612): the file is in hand.
      setAttachError(null);
      receipts.clear();
      if (uploadFailure) receipts.fail(file, uploadErrorMessage(uploadFailure));
      setType('expense');
      setEditing(null);
      // A new form from the reply, clamped where a `maxLength` cannot reach,
      // holding at most the one object this scan uploaded — none when the
      // upload failed. Anything else this sitting uploaded is the sweep's
      // (#119).
      setForm(scannedEntryForm(result, today(), currency, attachment, categoriesFor('expense')));
      setScan(result);
      setStep('form');
    } catch (err) {
      setScan({ error: err.message || 'The receipt could not be read.' });
      setStep('picker');
    } finally {
      setScanning(false);
      setScanUpload(null);
    }
  };

  return {
    step, setStep, type, isRevenue: type === 'revenue',
    form, patch, setForm, canSave, errors,
    editing, saving, guard, setGuard,
    scan, scanning, scanUpload, attachError,
    // Busy while a receipt is waiting or going up; the rows say which.
    attaching: receipts.busy,
    receiptUploads: receipts.items,
    retryReceipt: receipts.retry,
    cancelReceipt: receipts.cancel,
    dismissReceipt: receipts.dismiss,
    openPicker, openForm, openEdit, closeAll, leaveForm, save,
    attach, detach, scanReceipt,
  };
}
